import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../../Constants';

const WhatsAppConfig = () => {
  const { user } = useContext(AuthContext);
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;
  const token = user?.token || localStorage.getItem("token");

  // Updated state based on new payload structure
  const [config, setConfig] = useState({
    id: '', // Optional: populated if updating
    companyId: companyId,
    apiKey: '',
    baseUrl: '',
    domain: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showApiKey, setShowApiKey] = useState(false);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  // Fetch existing config on component mount
  useEffect(() => {
    if (companyId) {
      fetchConfig();
    }
  }, [companyId]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/whatsappConfig/${companyId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (response.ok && result.status === "success") {
        if (result.data && result.data.id) {
          setConfig({
            id: result.data.id,
            companyId: result.data.companyId || companyId,
            apiKey: result.data.apiKey || '',
            baseUrl: result.data.baseUrl || '',
            domain: result.data.domain || ''
          });
          setHasExistingConfig(true);
        }
      } else {
        // If status is error, it likely means config not found
        setMessage({ text: 'No existing configuration found. Please set one up.', type: 'info' });
        setHasExistingConfig(false);
      }
    } catch (error) {
      setMessage({ text: 'Error loading configuration. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });
    
    try {
      // Clean payload: remove empty id if it's a new config
      const payload = { ...config };
      if (!payload.id) delete payload.id;

      const response = await fetch(`${API_URL}/api/whatsappConfig/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      const result = await response.json();
      
      if (response.ok && result.status === "success") {
        setHasExistingConfig(true);
        setMessage({ 
          text: `Configuration ${hasExistingConfig ? 'updated' : 'saved'} successfully!`, 
          type: 'success' 
        });
        
        // Update state with returned data (especially the newly generated ID)
        if (result.data) {
          setConfig(prev => ({
            ...prev,
            id: result.data.id || prev.id,
            apiKey: result.data.apiKey || prev.apiKey,
            baseUrl: result.data.baseUrl || prev.baseUrl,
            domain: result.data.domain || prev.domain
          }));
        }
      } else {
        throw new Error(result.message || 'Failed to save configuration');
      }
    } catch (error) {
      setMessage({ text: error.message || 'Error saving configuration', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-[#FAF8FF] dark:bg-gray-900 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          
          {/* Header Section */}
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/30">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center">
              <svg className="w-6 h-6 mr-2 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.016a9.97 9.97 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.936 9.936 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.243-1.64a11.882 11.882 0 005.74 1.484h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.49"/>
              </svg>
              WhatsApp API Configuration
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {hasExistingConfig 
                ? 'Manage and update your WhatsApp API credentials.' 
                : 'Enter your credentials to connect your WhatsApp Business account.'}
            </p>
          </div>
          
          {/* Alert Messaging */}
          {message.text && (
            <div className={`mx-6 mt-4 p-4 rounded-md flex items-center ${
              message.type === 'success' ? 'bg-green-50 dark:bg-green-900/50 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' : 
              message.type === 'error' ? 'bg-red-50 dark:bg-red-900/50 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800' :
              'bg-blue-50 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800'
            }`}>
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          )}
          
          {/* Form Section */}
          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
            
            {/* Base URL (No Validation) */}
            <div>
              <label htmlFor="baseUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Base URL
              </label>
              <input
                type="text"
                id="baseUrl"
                name="baseUrl"
                value={config.baseUrl}
                onChange={handleChange}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2.5 border bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                placeholder="https://api.whatsapp.com/..."
              />
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">The primary endpoint for your WhatsApp API communication.</p>
            </div>
            
            {/* API Key */}
            <div>
              <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? "text" : "password"}
                  id="apiKey"
                  name="apiKey"
                  value={config.apiKey}
                  onChange={handleChange}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2.5 border pr-10 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                  placeholder="Enter your API key"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Keep this key secure. It acts as your authentication token.</p>
            </div>

            {/* Domain */}
            <div>
              <label htmlFor="domain" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Domain
              </label>
              <input
                type="text"
                id="domain"
                name="domain"
                value={config.domain}
                onChange={handleChange}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2.5 border bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                placeholder="yourdomain.com"
                required
              />
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">The verified domain associated with your business.</p>
            </div>
            
            {/* Form Actions */}
            <div className="pt-4 flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:focus:ring-offset-gray-800 disabled:opacity-70 disabled:cursor-not-allowed transition-all"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {hasExistingConfig ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {hasExistingConfig ? 'Update Configuration' : 'Save Configuration'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        
        {/* Info Section */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-lg p-5 text-sm text-blue-800 dark:text-blue-200">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-semibold mb-1">Configuration Guidelines</h3>
              <ul className="list-disc pl-5 space-y-1.5 opacity-90">
                <li>Your <strong>API Key</strong> should be kept strictly confidential.</li>
                <li>The <strong>Base URL</strong> must be the fully qualified URL provided by your API gateway.</li>
                <li>Updates to these credentials will apply immediately to outgoing messages.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConfig;